import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'

import { ApiError, apiFetch, fetchActivityPdf } from '../lib/api'

// Transcription activity — productivity and audit, over `qryTranscriptionProductivity`.
//
// Behavioural specification: `ActivityReportTranscriptionAudit.aspx` (the filter
// form: two dates, nothing else) → `ActivityReportPDFTranscription.aspx` (an
// iframe host) → `ActivityReportPDFTranscriptionSub.aspx` (the report), which
// selects `{qryTranscriptionProductivity.TranscriptionDate} in <begin> to <end>`
// and renders `Reports/TranscriptionAudi.rpt`.
//
// That template is not among the 55 converted to JSON under `oldcode/report/json/`,
// so its layout is unknown and **no PDF is produced here** — the data underneath
// it is: the same view, the same date filter, the detail rows and the totals.
// The two tabs split it by question rather than by legacy page: Productivity is
// who transcribed how much, Audit is turnaround and reports altered afterwards.
//
// Filter state lives in the URL, as on the activity report, so a report can be
// bookmarked and shared. The legacy kept it in Session, so it could not be.

interface Row {
  StudyID: number | null
  PatientID: string | null
  StudyDate: string | null
  StudyDateTime: string | null
  StudyLocation: string | null
  Transcriptionist: string | null
  TranscribedBy: number | null
  TranscriptionDate: string | null
  LastUpdated: string | null
  StatusID: number | null
  StudyStatus: string | null
  DaysToTranscription?: number | null
  EditedAfterTranscription?: number | null
  DaysToLastUpdate?: number | null
}
interface Page {
  rows: Row[]
  total: number
  page: number
  page_size: number
  pages: number
}
interface Named {
  name: string
  studies: number
}
interface Options {
  transcriptionists: Named[]
  locations: Named[]
  group_by: string[]
  sort_by: string[]
  exceptions: string[]
}
interface Group {
  group: string | null
  studies: number
  transcriptionists?: number
  first_transcription?: string | null
  last_transcription?: string | null
  avg_days_to_transcription?: number | null
  min_days_to_transcription?: number | null
  max_days_to_transcription?: number | null
  edited_after_transcription?: number
  late?: number
  not_completed?: number
}

type Tab = 'productivity' | 'audit'

const DETAIL_COLUMNS: { key: keyof Row; label: string; num?: boolean }[] = [
  { key: 'TranscriptionDate', label: 'Transcribed' },
  { key: 'Transcriptionist', label: 'Transcriptionist' },
  { key: 'StudyDateTime', label: 'Study Date' },
  { key: 'PatientID', label: 'ID#' },
  { key: 'StudyLocation', label: 'Location' },
  { key: 'StudyStatus', label: 'Status' },
  { key: 'StudyID', label: 'Study', num: true },
]

const AUDIT_COLUMNS: { key: keyof Row; label: string; num?: boolean }[] = [
  ...DETAIL_COLUMNS,
  { key: 'DaysToTranscription', label: 'Days to Transcribe', num: true },
  { key: 'LastUpdated', label: 'Last Updated' },
  { key: 'EditedAfterTranscription', label: 'Edited After' },
]

// The audit exception filters, with the question each one answers.
const EXCEPTION_LABELS: Record<string, string> = {
  edited: 'Edited after transcription',
  late: 'Slow turnaround',
  before_study: 'Transcribed before the study date',
  not_completed: 'Not marked Completed',
}

export default function TranscriptionReportPage() {
  const [params, setParams] = useSearchParams()
  const [options, setOptions] = useState<Options | null>(null)
  const [data, setData] = useState<Page | null>(null)
  const [groups, setGroups] = useState<Group[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const get = (k: string, d = '') => params.get(k) ?? d
  const tab: Tab = get('tab') === 'audit' ? 'audit' : 'productivity'
  const groupBy = get('by', 'Transcriptionist')

  useEffect(() => {
    apiFetch<Options>('/api/v1/activity/transcription/options')
      .then(setOptions)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])

  const [printing, setPrinting] = useState(false)

  // `ActivityReportPDFTranscription.aspx` framed a Crystal export; this is that
  // document. The audit tab is a different query and is not printed — the legacy
  // had no PDF for it either, only `TranscriptionAudi.rpt` over the productivity
  // view, which is what this prints.
  const print = useCallback(async () => {
    setPrinting(true)
    setError(null)
    try {
      const qs = new URLSearchParams(params)
      qs.delete('by')
      qs.delete('page')
      qs.delete('tab')
      qs.set('by', groupBy)
      const { url } = await fetchActivityPdf('activity/transcription', qs.toString())
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setPrinting(false)
    }
  }, [params, groupBy])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const qs = new URLSearchParams(params)
    qs.delete('by')
    qs.delete('tab')
    if (!qs.has('pageSize')) qs.set('pageSize', '50')
    // Only the audit tab has exceptions; carrying one onto the productivity tab
    // would silently narrow a report that has no control to clear it.
    if (tab !== 'audit') qs.delete('exception')
    const base = tab === 'audit' ? '/api/v1/activity/transcription/audit' : '/api/v1/activity/transcription'
    try {
      const [page, summary] = await Promise.all([
        apiFetch<Page>(`${base}?${qs.toString()}`),
        apiFetch<{ groups: Group[] }>(`${base}/summary?by=${groupBy}&${qs.toString()}`),
      ])
      setData(page)
      setGroups(summary.groups)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [params, groupBy, tab])

  useEffect(() => {
    void load()
  }, [load])

  function update(patch: Record<string, string>, resetPage = true) {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v === '') next.delete(k)
      else next.set(k, v)
    }
    if (resetPage) next.delete('page')
    setParams(next)
  }

  const columns = tab === 'audit' ? AUDIT_COLUMNS : DETAIL_COLUMNS
  const totalShown = groups?.reduce((a, g) => a + g.studies, 0) ?? 0

  return (
    <main className="page page-wide">
      {/* A sibling's strip, and a deliberate one (DEC-023).
          `ActivityReportTranscriptionAudit.aspx:17` is "Home  Log Off"; the one
          taken here is `ActivityReportPDFTranscription.aspx:17`, the page it
          hands off to, whose Reports link (`ActivityReportPDF1.aspx.vb:48`) is
          the only surviving route to the other activity report. */}
      <PageNav strip="activityTranscription" />
      <h1>Transcription Activity</h1>

      {error && <p className="banner banner-error">{error}</p>}

      <div className="tabs">
        <button
          type="button"
          className={tab === 'productivity' ? 'tab tab-on' : 'tab'}
          onClick={() => update({ tab: '' }, false)}
        >
          Productivity
        </button>
        <button
          type="button"
          className={tab === 'audit' ? 'tab tab-on' : 'tab'}
          onClick={() => update({ tab: 'audit' }, false)}
        >
          Audit
        </button>
      </div>

      <fieldset className="filters">
        <legend>Selection</legend>
        <div className="filter-row">
          {/* The legacy form's whole parameter set: two required dates on
              `TranscriptionDate`. Everything after them is an addition. */}
          <label>
            Transcribed From
            <input
              type="date"
              value={get('beginDate')}
              onChange={(e) => update({ beginDate: e.target.value })}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={get('endDate')}
              onChange={(e) => update({ endDate: e.target.value })}
            />
          </label>
          <label>
            Transcriptionist
            <select
              value={get('transcriptionist')}
              onChange={(e) => update({ transcriptionist: e.target.value })}
            >
              <option value="">All</option>
              {options?.transcriptionists.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name} ({t.studies.toLocaleString()})
                </option>
              ))}
            </select>
          </label>
          <label>
            Location
            <select
              value={get('studyLocation')}
              onChange={(e) => update({ studyLocation: e.target.value })}
            >
              <option value="">All</option>
              {options?.locations.map((l) => (
                <option key={l.name} value={l.name}>
                  {l.name} ({l.studies.toLocaleString()})
                </option>
              ))}
            </select>
          </label>
          <label>
            Patient ID
            <input
              defaultValue={get('patientId')}
              onBlur={(e) => update({ patientId: e.target.value })}
              onKeyDown={(e) =>
                e.key === 'Enter' && update({ patientId: e.currentTarget.value })
              }
            />
          </label>
          {tab === 'audit' && (
            <>
              <label>
                Exception
                <select
                  value={get('exception')}
                  onChange={(e) => update({ exception: e.target.value })}
                >
                  <option value="">None — all transcriptions</option>
                  {options?.exceptions.map((x) => (
                    <option key={x} value={x}>
                      {EXCEPTION_LABELS[x] ?? x}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Slow after (days)
                <input
                  type="number"
                  min={0}
                  value={get('lateAfterDays', '2')}
                  onChange={(e) => update({ lateAfterDays: e.target.value })}
                />
              </label>
            </>
          )}
          <button type="button" onClick={() => setParams(new URLSearchParams())}>
            Clear
          </button>
        </div>
        <p className="muted">
          No PDF: `TranscriptionAudi.rpt` was not among the Crystal templates converted,
          so its layout is unknown and is not approximated here.
        </p>
      </fieldset>

      <div className="cols">
        <section>
          <div className="grid-meta">
            <label className="inline-label">
              Totals by{' '}
              <select value={groupBy} onChange={(e) => update({ by: e.target.value }, false)}>
                {(options?.group_by ?? []).map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" onClick={() => void print()} disabled={printing}>
              {printing ? 'Printing…' : 'Print'}
            </button>
          </div>
          <div className="grid-scroll">
            <table className="grid">
              <thead>
                {tab === 'audit' ? (
                  <tr>
                    <th>{groupBy}</th>
                    <th className="num">Studies</th>
                    <th className="num">Avg Days</th>
                    <th className="num">Max Days</th>
                    <th className="num">Edited</th>
                    <th className="num">Slow</th>
                    <th className="num">Not Completed</th>
                  </tr>
                ) : (
                  <tr>
                    <th>{groupBy}</th>
                    <th className="num">Studies</th>
                    <th>First</th>
                    <th>Last</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {groups?.slice(0, 40).map((g) => (
                  <tr key={String(g.group)}>
                    <td>{g.group?.trim() || <span className="muted">(blank)</span>}</td>
                    <td className="num">{g.studies.toLocaleString()}</td>
                    {tab === 'audit' ? (
                      <>
                        <td className="num">{fmtDays(g.avg_days_to_transcription)}</td>
                        <td className="num">{g.max_days_to_transcription ?? ''}</td>
                        <td className="num">{(g.edited_after_transcription ?? 0).toLocaleString()}</td>
                        <td className="num">{(g.late ?? 0).toLocaleString()}</td>
                        <td className="num">{(g.not_completed ?? 0).toLocaleString()}</td>
                      </>
                    ) : (
                      <>
                        <td>{fmtDate(g.first_transcription ?? null)}</td>
                        <td>{fmtDate(g.last_transcription ?? null)}</td>
                      </>
                    )}
                  </tr>
                ))}
                {groups && groups.length > 0 && (
                  <tr className="total-row">
                    <td>Total</td>
                    <td className="num">{totalShown.toLocaleString()}</td>
                    <td colSpan={tab === 'audit' ? 5 : 2} />
                  </tr>
                )}
                {groups && groups.length === 0 && (
                  <tr>
                    <td colSpan={tab === 'audit' ? 7 : 4} className="muted">
                      No transcriptions match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {groups && groups.length > 40 && (
            <p className="muted">Showing the 40 largest of {groups.length} groups.</p>
          )}
        </section>

        <section>
          <div className="grid-meta">
            {loading ? 'Loading…' : data ? `${data.total.toLocaleString()} transcriptions` : ''}
            {data && data.pages > 1 && (
              <span className="muted">
                {' '}
                · page {data.page} of {data.pages.toLocaleString()}
              </span>
            )}
          </div>
          <div className="grid-scroll">
            <table className="grid">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className={c.num ? 'num' : undefined}
                      onClick={() => sortBy(c.key)}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.rows.map((r) => (
                  <tr key={r.StudyID ?? `${r.PatientID}-${r.TranscriptionDate}`}>
                    {columns.map((c) => (
                      <td key={c.key} className={c.num ? 'num' : undefined}>
                        {cell(r, c.key)}
                      </td>
                    ))}
                  </tr>
                ))}
                {data && data.rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={columns.length} className="muted">
                      No transcriptions match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {data && data.pages > 1 && (
            <div className="pager">
              <button
                type="button"
                disabled={data.page <= 1}
                onClick={() => update({ page: String(data.page - 1) }, false)}
              >
                Previous
              </button>
              <span className="muted">
                {data.page} / {data.pages.toLocaleString()}
              </span>
              <button
                type="button"
                disabled={data.page >= data.pages}
                onClick={() => update({ page: String(data.page + 1) }, false)}
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  )

  function sortBy(key: keyof Row) {
    const column = key === 'StudyDateTime' ? 'StudyDate' : key
    if (!options?.sort_by.includes(column as string)) return
    const same = get('sort', 'TranscriptionDate') === column
    update(
      {
        sort: column as string,
        direction: same && get('direction', 'DESC') === 'DESC' ? 'ASC' : 'DESC',
      },
      false,
    )
  }
}

function cell(row: Row, key: keyof Row) {
  const v = row[key]
  if (key === 'EditedAfterTranscription') return v ? 'yes' : ''
  if (key === 'TranscriptionDate' || key === 'LastUpdated') return fmtDateTime(v as string | null)
  if (key === 'StudyDateTime') return fmtDate(v as string | null)
  return v === null || v === undefined ? '' : String(v)
}

function fmtDate(v: string | null) {
  return v ? v.slice(0, 10) : ''
}

function fmtDateTime(v: string | null) {
  return v ? `${v.slice(0, 10)} ${v.slice(11, 16)}` : ''
}

function fmtDays(v: number | null | undefined) {
  return v === null || v === undefined ? '' : v.toFixed(1)
}
